import type { Analytics } from 'firebase/analytics'
import {
  isMarketingTrackingAllowed,
  isTrackingEnvironment,
  logMetaEvent,
  logMetaPageView,
} from './marketing'

// Analytics (GA4) is only supported in browser environments with the required
// APIs (cookies, indexedDB). isSupported() guards init the same way messaging
// does, so SSR/unsupported contexts resolve to null instead of throwing.
//
// PRODUCTION ONLY. Until 2026-08-10 this initialized wherever the browser
// supported it, which included `npm run dev` on a developer's laptop. Local
// page loads were being recorded as real users, sessions and page views in the
// live GA property. Two independent conditions, because either one alone has
// a hole: PROD is false under `vite dev` but true for the prerenderer's local
// server, and the hostname check catches that plus any `vite preview` run.
// The hostname check also excludes *.web.app and *.firebaseapp.com (preview
// channels and the raw Firebase URLs), which are not the real site.
const ANALYTICS_ENVIRONMENT_ENABLED = isTrackingEnvironment()

type AnalyticsModule = typeof import('firebase/analytics')

interface AnalyticsClient {
  mod: AnalyticsModule
  analytics: Analytics
}

let clientPromise: Promise<AnalyticsClient | null> | null = null

// ─── URL redaction ─────────────────────────────────────────────────────────
//
// Some URLs carry identifiers that must never reach GA:
//   /waiver/:eventId/:attendeeId?t=<token>  the attendee segment is often a
//                                           Stripe checkout session id, and
//                                           t is a signing token
//   ?session_id=cs_live_...                 Stripe success returns
// Waiver URLs collapse to /waiver/:eventId with no query string; session_id is
// stripped from every other URL. The hash is always dropped.
const WAIVER_PATH = /^\/waiver\/([^/]+)\/.+$/

export function redactPath(pathWithQuery: string): string {
  const url = new URL(pathWithQuery, 'https://redact.invalid')
  const waiver = WAIVER_PATH.exec(url.pathname)
  if (waiver) return `/waiver/${waiver[1]}`
  url.searchParams.delete('session_id')
  const query = url.searchParams.toString()
  return url.pathname + (query ? `?${query}` : '')
}

function redactedLocation(): string {
  return window.location.origin + redactPath(window.location.pathname + window.location.search)
}

// gtag tracks page_referrer itself and would carry the previous raw URL (a
// waiver link, say) onto the next hit, so the referrer is set explicitly: the
// previous page's redacted location for in-app navigation, or the landing
// document.referrer (same-origin redacted, Stripe reduced to its origin).
function redactedLandingReferrer(): string {
  if (typeof document === 'undefined' || !document.referrer) return ''
  try {
    const ref = new URL(document.referrer)
    if (ref.origin === window.location.origin) return ref.origin + redactPath(ref.pathname + ref.search)
    if (/(^|\.)stripe\.com$/.test(ref.hostname)) return `${ref.origin}/`
    return document.referrer
  } catch {
    return ''
  }
}

let lastPageLocation: string | null = null
let pageReferrer = typeof window !== 'undefined' ? redactedLandingReferrer() : ''

// Advance the referrer only when the page actually changed (logPageView also
// re-fires for the same page when consent is granted).
function notePageView(): void {
  const location = redactedLocation()
  if (lastPageLocation !== null && lastPageLocation !== location) pageReferrer = lastPageLocation
  lastPageLocation = location
}

// ─── Internal traffic ──────────────────────────────────────────────────────
//
// Events from these signed-in accounts carry traffic_type: 'internal' so the
// GA internal-traffic data filter can exclude them. Portal members are never
// tagged. Auth loads after the first paint on public pages, so the last known
// state is kept in localStorage and applied to events that fire before auth
// resolves; AuthContext corrects it as soon as the real user is known.
const INTERNAL_TRAFFIC_EMAILS = [
  'scott@advantagehorsemanship.com',
  'allie@advantagehorsemanship.com',
]
const INTERNAL_TRAFFIC_HINT_KEY = 'ah_internal_traffic_v1'

function readInternalTrafficHint(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(INTERNAL_TRAFFIC_HINT_KEY) === '1'
  } catch {
    return false
  }
}

let internalTraffic = readInternalTrafficHint()

// Parameters that must ride on every hit, including the ones gtag sends on its
// own (enhanced measurement scrolls, clicks, etc.): gtag('set') via
// setDefaultEventParameters covers those; manual events also merge them in.
function trackingParams(): Record<string, unknown> {
  return {
    page_location: redactedLocation(),
    page_referrer: pageReferrer || undefined,
    traffic_type: internalTraffic ? 'internal' : undefined,
  }
}

function applyDefaultParams(client: AnalyticsClient): void {
  client.mod.setDefaultEventParameters(trackingParams())
}

// Called by AuthContext whenever the signed-in user changes (null on sign-out).
export function setAnalyticsUser(email: string | null): void {
  const next = !!email && INTERNAL_TRAFFIC_EMAILS.includes(email.toLowerCase())
  try {
    if (next) window.localStorage.setItem(INTERNAL_TRAFFIC_HINT_KEY, '1')
    else window.localStorage.removeItem(INTERNAL_TRAFFIC_HINT_KEY)
  } catch {
    // storage unavailable; the in-memory flag below still applies
  }
  if (next === internalTraffic) return
  internalTraffic = next
  // Only touch GA if it is already loaded; never load it just for this.
  clientPromise?.then((client) => {
    if (client) applyDefaultParams(client)
  })
}

// The GA SDK (and firebase/app underneath it) is loaded on demand, and only
// once consent is granted. Nothing here is in the critical path of a public
// page: this module is safe to import from the initial bundle.
function getAnalyticsClient(): Promise<AnalyticsClient | null> {
  if (!ANALYTICS_ENVIRONMENT_ENABLED || !isMarketingTrackingAllowed()) {
    return Promise.resolve(null)
  }
  if (!clientPromise) {
    clientPromise = Promise.all([import('firebase/analytics'), import('./firebaseApp')])
      .then(async ([mod, { app }]) => {
        const supported = await mod.isSupported()
        if (!supported) return null
        // Defaults are set before initializeAnalytics so they are in place
        // before gtag sends anything, automatic hits included.
        mod.setDefaultEventParameters(trackingParams())
        return {
          mod,
          analytics: mod.initializeAnalytics(app, { config: { send_page_view: false } }),
        }
      })
      .catch(() => null)
  }
  return clientPromise
}

// Fire a GA4 page_view. A single-page app does not reload between routes, so
// GA4 never auto-fires page_view after the first load — the router calls this
// on every route change to record real navigation. `path` is redacted before
// it is sent (see redactPath).
export function logPageView(path: string): void {
  notePageView()
  // Captured now, not when the SDK resolves, so a fast second navigation
  // cannot relabel this hit with the next page's URL.
  const defaults = trackingParams()
  const params = { ...defaults, page_path: redactPath(path), page_title: document.title }
  getAnalyticsClient().then((client) => {
    if (!client) return
    // Page-level defaults for the automatic hits gtag sends on this page.
    client.mod.setDefaultEventParameters(defaults)
    client.mod.logEvent(client.analytics, 'page_view', params)
  })
  logMetaPageView()
}

// Fire an arbitrary GA4 event (conversions / funnel signals such as
// begin_checkout, start_trial, purchase, generate_lead). Same isSupported guard
// as page views, so SSR/unsupported contexts are a no-op. Marked as key events
// in GA Admin so they surface as conversions in the dashboard analytics.
export function logGaEvent(name: string, params?: Record<string, unknown>): void {
  const gaParams = { ...params, ...trackingParams() }
  getAnalyticsClient().then((client) => {
    if (!client) return
    client.mod.logEvent(client.analytics, name, gaParams)
  })
  logMetaEvent(name, params)
}

// ─── Purchase ──────────────────────────────────────────────────────────────

export interface VerifiedPurchaseItem {
  item_id: string
  item_name: string
  price: number
  quantity: number
}

/** Purchase details read server-side from the Stripe checkout session. */
export interface VerifiedPurchase {
  transactionId: string
  value: number
  currency: string
  items: VerifiedPurchaseItem[]
}

// Fire purchase once per Stripe checkout session. The sessionStorage guard
// keyed on transaction_id stops a refresh (or a re-render) from double
// counting. value/currency/items come only from the server's read of the
// Stripe session, never from the URL. If the server could not supply them the
// conversion is still counted, just without revenue.
export function logPurchase(transactionId: string, verified: VerifiedPurchase | null): void {
  const guardKey = `ah_purchase_${transactionId}`
  try {
    if (window.sessionStorage.getItem(guardKey)) return
    window.sessionStorage.setItem(guardKey, '1')
  } catch {
    // sessionStorage unavailable; still fire below.
  }
  logGaEvent(
    'purchase',
    verified
      ? {
          transaction_id: verified.transactionId,
          value: verified.value,
          currency: verified.currency,
          items: verified.items,
        }
      : { transaction_id: transactionId, currency: 'USD' },
  )
}
